/**
 * «Профиль» — PC audit 28.09, point 13: the zone and the role read as words
 * («Москва (UTC+3)», «Участник»), the zone can be changed here, and the screen
 * is built like the rest of the app.
 */

import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, ChevronRight, Plug, Wrench } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { SelectField } from "@/components/ui/Field";
import { SkeletonList } from "@/components/ui/Skeleton";
import { useToast } from "@/components/ui/Toast";
import { Me } from "@/api/resources";
import { clearTokens } from "@/boot/auth";
import { isInTelegram } from "@/boot/telegram";
import { Caption, EmptyNote, ScreenHeader } from "@/kit";
import { deviceZone, zoneLabel, zoneOptions } from "@/lib/timezones";

interface Props {
  onSignOut?: () => void;
}

const ROLE: Record<string, string> = { admin: "Администратор", member: "Участник" };

function LinkRow({ to, icon, title, sub }: { to: string; icon: ReactNode; title: string; sub: string }) {
  return (
    <li className="border-b border-p-border last:border-0">
      <Link to={to} className="flex items-center gap-3 px-4 py-3 press-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-p-focus">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-p-border bg-p-surface-2 text-p-text" aria-hidden="true">{icon}</span>
        <span className="min-w-0 flex-1">
          <span className="block text-[15px] font-semibold leading-5 text-p-text">{title}</span>
          <span className="block text-meta text-p-text-muted">{sub}</span>
        </span>
        <ChevronRight className="h-5 w-5 shrink-0 text-p-text-muted" aria-hidden="true" />
      </Link>
    </li>
  );
}

export function ProfileView({ onSignOut }: Props) {
  const qc = useQueryClient();
  const toast = useToast();
  const me = useQuery({ queryKey: ["me"], queryFn: Me.get });
  const inTelegram = isInTelegram();
  const device = deviceZone();
  const setZone = useMutation({
    mutationFn: (timezone: string) => Me.update({ timezone }),
    onSuccess: (user) => {
      qc.setQueryData(["me"], user);
      // The notification settings keep the same zone (the server moves it too).
      void qc.invalidateQueries({ queryKey: ["notification-settings"] });
      toast.success(`Часовой пояс: ${zoneLabel(user.timezone)}`);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (me.isLoading) return <div className="flex flex-col gap-3"><ScreenHeader title="Профиль" /><SkeletonList count={3} /></div>;
  if (me.isError || !me.data) return <div className="flex flex-col gap-3"><ScreenHeader title="Профиль" /><EmptyNote>Не удалось загрузить профиль. Проверь связь.</EmptyNote></div>;

  const u = me.data;
  const handleSignOut = () => {
    clearTokens();
    onSignOut?.();
  };

  return (
    <div className="flex flex-col gap-3">
      <ScreenHeader title="Профиль" />

      <ul className="overflow-hidden rounded-card border border-p-border bg-p-surface">
        <li className="px-4 py-3">
          <span className="block truncate text-[17px] font-semibold leading-6 text-p-text">{u.display_name}</span>
          <span className="block text-meta text-p-text-muted">
            {u.telegram_username ? `@${u.telegram_username} · ` : ""}{ROLE[u.role] ?? "Участник"}
          </span>
        </li>
      </ul>

      <Caption>Часовой пояс</Caption>
      <SelectField
        label="Где ты сейчас"
        value={u.timezone}
        options={zoneOptions([u.timezone, device])}
        onChange={(tz) => { if (tz && tz !== u.timezone) setZone.mutate(tz); }}
        disabled={setZone.isPending}
        hint="По нему считаются «сегодня», сроки и тихие часы уведомлений."
      />
      {device && device !== u.timezone && (
        <Button size="compact" variant="secondary" className="self-start" loading={setZone.isPending} onClick={() => setZone.mutate(device)}>
          Как на этом устройстве: {zoneLabel(device)}
        </Button>
      )}

      <Caption>Настройки</Caption>
      <ul className="overflow-hidden rounded-card border border-p-border bg-p-surface">
        <LinkRow to="/settings/notifications" icon={<Bell className="h-[18px] w-[18px]" />} title="Уведомления бота" sub="что присылать, тихие часы, пауза" />
        <LinkRow to="/settings/agents" icon={<Plug className="h-[18px] w-[18px]" />} title="Подключения агентов" sub="кто работает за тебя и что ему можно" />
        <LinkRow to="/tools" icon={<Wrench className="h-[18px] w-[18px]" />} title="Инструменты" sub="реестр инструментов команды" />
      </ul>

      {!inTelegram && (
        <Button variant="secondary" block onClick={handleSignOut} aria-label="Выйти из аккаунта">Выйти</Button>
      )}
    </div>
  );
}
